import {InvoiceRepo} from "../api/invoice.repo";
import {inject, Injectable} from "@angular/core";
import {OrgDto} from "../dto/org.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {forkJoin, Observable} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {SearchInvoiceRes} from "../dto/search/search-invoice.res";
import {buildLetterAddress, DuckDbHelper, sqlDirection, sqlLike, sqlNumber, sqlText, sqlYesNo} from "./duckdb.helper";
import {isDefined, nonBlank} from "../../libs/utils/epic.utils";
import {map} from "rxjs/operators";
import {LoggingService} from "../../services/logger/logging.service";
import {Additionals} from "../item/additionals";
import {InvoiceDto} from "../dto/document/invoice.dto";
import {DocumentContactDto} from "../dto/document-contact.dto";
import {SearchFilterResponse} from "../dto/search/search-table.dto.res";

@Injectable({providedIn: 'root'})
export class InvoiceDuckDb extends InvoiceRepo {

  private readonly duck = inject(DuckDbHelper);
  private readonly log = inject(LoggingService);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public override getById(_org: OrgDto, invoiceId: string, _add?: Additionals): Observable<WrapperDto<InvoiceDto>> {
    // Der Export fuehrt keine Sterbefaelle: weder eine Tabelle `death` noch eine Spalte `invoice.death_id`.
    // Das Feld bleibt als NULL erhalten, damit InvoiceDto und die Detailseite unveraendert weiterlaufen.
    const selectInvoice = `
      SELECT i.id                                                   AS id,
             i.state                                                AS state,
             i.type                                                 AS type,
             i.number                                               AS number,
             i.invoice_date                                         AS invoiceDate,
             i.order_date                                           AS orderDate,
             i.performance_date                                     AS performanceDate,
             i.performance_date_to                                  AS performanceDateTo,
             i.total_gross_amount                                   AS totalGrossAmount,
             i.total_net_amount                                     AS totalNetAmount,
             i.total_vat_amount                                     AS totalVatAmount,
             i.currency_code                                        AS currencyCode,
             i.digits                                               AS fractionalDigits,
             i.media_id                                             AS mediaId,
             (CASE WHEN cancel.id IS NULL THEN FALSE ELSE TRUE END) AS cancelled,
             cancel.id                                              AS cancelledById,
             cancel.media_id                                        AS cancelledByMediaId,
             cancelOrigin.id                                        AS cancellationOriginId,
             cancelOrigin.media_id                                  AS cancellationOriginMediaId,
             i.total_gross_amount_rounding                          AS totalGrossAmountRounding,
             i.total_gross_amount_rounded                           AS totalGrossAmountRounded,
             CAST(NULL AS VARCHAR)                                  AS deathId
      FROM invoice i
             LEFT JOIN invoice cancel ON cancel.cancellation_origin_id = i.id
             LEFT JOIN invoice cancelOrigin ON cancelOrigin.id = i.cancellation_origin_id
      WHERE i.id = ${sqlText(invoiceId)}`;

    const selectPosition = `
      SELECT p.id                             AS id,
             p.type                           AS type,
             p.pos                            AS pos,
             p.number                         AS number,
             p.quantity                       AS quantity,
             p.net_amount                     AS netAmount,
             p.gross_amount                   AS grossAmount,
             p.total_net_amount               AS totalNetAmount,
             p.total_gross_amount             AS totalGrossAmount,
             p.total_vat_amount               AS totalVatAmount,
             p.total_net_amount_no_discount   AS totalNetAmountWithoutDiscount,
             p.total_gross_amount_no_discount AS totalGrossAmountWithoutDiscount,
             p.total_vat_amount_no_discount   AS totalVatAmountWithoutDiscount,
             p.text                           AS text,
             p.text_tpl                       AS textTpl
      FROM invoice_position p
      WHERE p.invoice_id = ${sqlText(invoiceId)}`;

    const selectDiscount = `
      SELECT d.id                 AS id,
             d.type               AS type,
             d.value              AS value,
             d.net_amount         AS netAmount,
             d.gross_amount       AS grossAmount,
             d.vat_amount         AS vatAmount,
             d.total_net_amount   AS totalNetAmount,
             d.total_gross_amount AS totalGrossAmount,
             d.total_vat_amount   AS totalVatAmount,
             d.position_id
      FROM invoice_position p
             JOIN invoice_discount d ON d.position_id = p.id
      WHERE p.invoice_id = ${sqlText(invoiceId)}`;

    const selectArticle = `
      SELECT a.id                   AS id,
             a.number               AS articleNumber,
             a.name                 AS name,
             a.unit                 AS unit,
             a.selling_price_net    AS sellingPriceNet,
             a.purchasing_price_net AS purchasingPriceNet,
             a.gross_amount         AS grossAmount,
             a.vat_amount           AS vatAmount,
             a.final_net_amount     AS finalNetAmount,
             a.final_gross_amount   AS finalGrossAmount,
             a.final_vat_amount     AS finalVatAmount,
             a.currency_code        AS currencyCode,
             a.vat_rate             AS vatRate,
             a.vat_rate_value       AS vatRateValue,
             a.old_part             AS oldPart,
             a.discountable         AS discountable,
             a.article_id           AS articleId,
             a.position_id
      FROM invoice_position p
             LEFT JOIN invoice_article a ON a.position_id = p.id
      WHERE p.invoice_id = ${sqlText(invoiceId)}`;

    const selectVat = `
      SELECT v.id                       AS id,
             v.vat_rate                 AS vatRate,
             v.vat_rate_value           AS vatRateValue,
             v.net_amount               AS netAmount,
             v.gross_amount             AS grossAmount,
             v.vat_amount               AS vatAmount,
             v.net_amount_no_discount   AS netAmountWithoutDiscount,
             v.gross_amount_no_discount AS grossAmountWithoutDiscount,
             v.vat_amount_no_discount   AS vatAmountWithoutDiscount,
             CASE
               WHEN v.vat_rate = 'OLD_PART_STANDARD'
                 OR v.vat_rate = 'OLD_PART_CREDIT' THEN TRUE
               ELSE FALSE END           AS oldPart
      FROM invoice_vat v
      WHERE v.invoice_id = ${sqlText(invoiceId)}`;

    const selectContact = `
      SELECT c.id               AS id,
             c.type             AS type,
             c.manual_input     AS manualInput,
             c.contact_type     AS contactType,
             c.number           AS number,
             c.full_name        AS fullName,
             c.street           AS street,
             c.house_number     AS houseNumber,
             c.address_suffix   AS addressSuffix,
             c.city             AS city,
             c.zip_code         AS zipCode,
             c.post_office_box  AS postOfficeBox,
             c.country_alpha2   AS countryAlpha2,
             c.contact_id       AS contactId,
             c.vehicle_id       AS vehicleId,
             c.vehicle_odometer AS vehicleOdometer,
             c.vehicle_history  AS vehicleHistory,
             FALSE              AS completeAsLetter
      FROM invoice_contact c
             LEFT JOIN invoice i ON i.contact_id = c.id
      WHERE i.id = ${sqlText(invoiceId)}`;

    const selectPayment = `
      SELECT p.id        AS id,
             p.completed AS completed,
             p.balance   AS balance,
             p.amount    AS amount,
      FROM payment p
             LEFT JOIN invoice i ON i.payment_id = p.id
      WHERE i.id = ${sqlText(invoiceId)}
        AND p.belong = 'INVOICE'`;

    const selectPaymentEntry = `
      SELECT e.id     AS id,
             e.amount AS amount,
             e.method AS method,
             e.date AS 'date', e.bank_address_id AS bankAddressId,
             FALSE    AS editable,
             e.payment_id
      FROM payment p
             LEFT JOIN invoice i ON i.payment_id = p.id
             LEFT JOIN payment_entry e ON e.payment_id = p.id
      WHERE i.id = ${sqlText(invoiceId)}
        AND p.belong = 'INVOICE'`;

    return forkJoin([
      this.duck.sql(selectInvoice),
      this.duck.sql(selectPosition),
      this.duck.sql(selectArticle),
      this.duck.sql(selectVat),
      this.duck.sql(selectContact),
      this.duck.sql(selectPayment),
      this.duck.sql(selectPaymentEntry),
      this.duck.sql(selectDiscount),
    ]).pipe(map(([invoiceRes, posRes, articleRes, vatRes, contactRes, paymentRes, entryRes, discountRes]) => {
      if (DuckDbHelper.hasRes(invoiceRes)) {
        posRes.filter((p: any) => isDefined(articleRes.find((a: any) => a.position_id === p.id)))
          .forEach((p: any) => p.article = articleRes.find((a: any) => a.position_id === p.id));
        posRes.forEach((p: any) => p.discount = discountRes.find((d: any) => d.position_id === p.id));
        invoiceRes[0].positions = posRes;
        invoiceRes[0].vats = vatRes;
        const contact = DuckDbHelper.firstRes(contactRes);
        if (isDefined(contact)) {
          contact.letterAddress = buildLetterAddress(contact);
          invoiceRes[0].contact = contact;
        }
        const payment = DuckDbHelper.firstRes(paymentRes);
        if (isDefined(payment)) {
          payment.entries = entryRes;
          invoiceRes[0].payment = payment;
        }
        const invoice = new InvoiceDto(invoiceRes[0]);
        return DuckDbHelper.data('InvoiceDto', invoice);
      }
      return DuckDbHelper.data('InvoiceDto', new InvoiceDto());
    }));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public override getContactById(org: OrgDto, invoiceId: string, _documentContactId: string): Observable<WrapperDto<DocumentContactDto>> {
    return this.getById(org, invoiceId).pipe(map((wi) => {
      const invoice = wi.getFirstData();
      return DuckDbHelper.data('DocumentContactDto', invoice.contact);
    }));
  }

  public override search(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchInvoiceRes>> {
    const limit = request.pageSize;
    const offset = request.pageSize * (request.page - 1);
    const sort = this.buildSort(request);
    const query = this.buildQuery(request);
    const filter = this.buildFilter(request);
    // Wie in getById: kein `death`, kein `invoice.death_id`. Die drei Felder bleiben als NULL erhalten,
    // weil die Rechnungsliste eine Spalte `deathFullName` fuehrt und sie sonst gar nicht erst laedt.
    const search = `
      SELECT i.id                                                          AS id,
             i.state                                                       AS state,
             i.number                                                      AS number,
             i.invoice_date                                                AS invoiceDate,
             COALESCE(i.total_gross_amount_rounded, i.total_gross_amount)  AS totalGrossAmount,
             i.total_net_amount                                            AS totalNetAmount,
             i.media_id                                                    AS mediaId,
             (CASE WHEN cancel.id IS NULL THEN FALSE ELSE TRUE END)        AS cancelled,
             cancel.id                                                     AS cancelledById,
             cancel.media_id                                               AS cancelledByMediaId,
             cancelOrigin.id                                               AS cancellationOriginId,
             cancelOrigin.media_id                                         AS cancellationOriginMediaId,
             payment.id                                                    AS paymentId,
             payment.completed                                             AS paymentCompleted,
             payment.balance                                               AS paymentBalance,
             i.currency_code                                               AS currencyCode,
             c.id                                                          AS dcId,
             c.type                                                        AS dcType,
             c.contact_id                                                  AS dcContactId,
             c.contact_type                                                AS dcContactType,
             c.number                                                      AS dcContactNumber,
             c.full_name                                                   AS dcContactFullName,
             c.manual_input                                                AS dcContactManualInput,
             c.vehicle_id                                                  AS dcContactVehicleId,
             TRIM(COALESCE(v.license_plate, '') || ' ' || COALESCE(v.manufacturer_name, '') || ' ' ||
                  COALESCE(v.model_name, ''))                              AS dcContactVehicleName,
             CAST(NULL AS VARCHAR)                                         AS deathId,
             CAST(NULL AS VARCHAR)                                         AS deathDeceasedId,
             CAST(NULL AS VARCHAR)                                         AS deathFullName,
             COUNT(*)                                                         OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING)               AS total_results
      FROM invoice i
             LEFT JOIN invoice_contact c ON c.id = i.contact_id
             LEFT JOIN vehicle v ON v.id = c.vehicle_id
             LEFT JOIN invoice cancel ON cancel.cancellation_origin_id = i.id
             LEFT JOIN invoice cancelOrigin ON cancelOrigin.id = i.cancellation_origin_id
             LEFT JOIN payment ON payment.id = i.payment_id
      WHERE ${isDefined(query) ? query : '1=1'}
        AND ${isDefined(filter) ? filter : '1=1'}
      ORDER BY ${sort} LIMIT ${sqlNumber(limit, 20)}
      OFFSET ${sqlNumber(offset, 0)}`;
    return this.duck.sql(search).pipe(map((res) => {
      const hasRes = isDefined(res) && res.length > 0;
      const firstRow = hasRes ? res[0] : {total_results: 0} as any;
      const result = new SearchInvoiceRes({
        items: hasRes ? res : [],
        hits: firstRow.total_results,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: request.filters.map((f) => new SearchFilterResponse(f))
      });
      return DuckDbHelper.data('SearchInvoiceRes', result);
    }));
  }

  private buildSort(request: SearchRequest): string {
    let field;
    switch (request.sortField) {
      case 'invoiceDate':
        field = 'i.invoice_date';
        break;
      case 'number':
        field = 'i.number';
        break;
      case 'totalNetAmount':
        field = 'i.total_net_amount';
        break;
      case 'dcContactFullName':
        field = 'c.full_name';
        break;
      case 'dcContactVehicleName':
        field = 'v.license_plate';
        break;
      case 'totalGrossAmount':
        field = 'COALESCE(i.total_gross_amount_rounded, i.total_gross_amount)';
        break;
      default:
        this.log.warn('Unhandled sort field "{}"', [request.sortField]);
        field = 'i.invoice_date';
    }
    return field + ' ' + sqlDirection(request.sortOrder);
  }

  private buildQuery(request: SearchRequest): string | undefined {
    if (isDefined(request.query) && nonBlank(request.query)) {
      return `(i.number ILIKE ${sqlLike(request.query)}
          OR c.full_name ILIKE ${sqlLike(request.query)}
          OR v.license_plate ILIKE ${sqlLike(request.query)}
          OR v.manufacturer_name ILIKE ${sqlLike(request.query)}
          OR v.model_name ILIKE ${sqlLike(request.query)})`;
    }
    return undefined;
  }

  private buildFilter(request: SearchRequest): string | undefined {
    const filters: string[] = [];
    const stateFilter = request.filters.find((f) => f.id === 'state');
    if (isDefined(stateFilter) && isDefined(stateFilter.selected) && stateFilter.selected?.length > 0) {
      const selected = stateFilter.selected.map((s: string) => sqlText(s)).join(',');
      filters.push('i.state IN (' + selected + ')');
    }
    filters.push(...[sqlYesNo(request, 'payment', 'payment.completed = TRUE', 'payment.completed = FALSE'),
      sqlYesNo(request, 'cancelled', 'cancel.id IS NOT NULL', 'cancel.id IS NULL')].filter((f) => isDefined(f)));
    // noinspection DuplicatedCode see ReceiptDuckDb
    const dcContactIdFilter = request.filters.find((f) => f.id === 'dcContactId');
    if (isDefined(dcContactIdFilter) && isDefined(dcContactIdFilter.selected) && dcContactIdFilter.selected?.length > 0) {
      filters.push(`c.contact_id = ${sqlText(dcContactIdFilter.selected[0])}`);
    }
    // Facettenfilter wie dcContactId darueber: Eine geleerte Typeahead-Auswahl bedeutet "nicht filtern".
    // Ohne die Laengenpruefung stuende hier `c.vehicle_id = NULL` und die Liste bliebe wortlos leer.
    const dcContactVehicleIdFilter = request.filters.find((f) => f.id === 'dcContactVehicleId');
    if (isDefined(dcContactVehicleIdFilter) && isDefined(dcContactVehicleIdFilter.selected) && dcContactVehicleIdFilter.selected?.length > 0) {
      filters.push(`c.vehicle_id = ${sqlText(dcContactVehicleIdFilter.selected[0])}`);
    }
    // Wie in MediaDuckDb: Der Export fuehrt keine Sterbefaelle und deshalb keine Spalte death_id. Den
    // Filter ersatzlos zu streichen waere schlimmer als ein Fehler: Der Rechnungsreiter eines Falls
    // listete dann SAEMTLICHE Rechnungen des Exports - die Rechnungen fremder Familien am eigenen Fall.
    const deathIdFilter = request.filters.find((f) => f.id === 'deathId');
    if (isDefined(deathIdFilter) && isDefined(deathIdFilter.selected) && deathIdFilter.selected?.length > 0) {
      filters.push('1=0');
    }
    return filters.length > 0 ? filters.join(' AND ') : undefined;
  }
}
